/**
 * What the campaigns screen says, where a runner can reach it — for the reason
 * `overview-copy.ts` gives. The screen renders these and decides nothing about them.
 *
 * Two things are never said, and `campaign-copy.test.ts` holds both: anything about
 * spending limits or spend, and any budget turned into money. A budget is the number
 * X holds, with its unit named.
 *
 * The same file lives in ttads; only the platform's fields differ.
 */
import type { Campaign, FundingInstrument, XAccount } from "./api.ts";
import type { TargetStatus } from "./campaigns.ts";
import type { XHost } from "./x-host.ts";

/**
 * `page.nextCursor !== null` — X answered a next page beside this one.
 *
 * It no longer says "and it will stay short": Load more reads the next page. What
 * it still refuses to say is how much more there is. X's `with_total_count` is
 * documented as exclusive with `cursor`, so a screen that pages has no total to
 * show, and a number invented from pages loaded times page size would be a guess
 * wearing a figure's clothes.
 *
 * It does not say «page»: «page» is the table's own, over the rows read (`NEXT_PAGE`,
 * lib/table-copy.ts).
 */
export const MORE_BANNER = {
	title: "This list is not complete.",
	body: "X has more campaigns under this account than the ones read so far. Load more reads the next ones from X. How many there are in total is something X does not say while paging, so this screen does not say it either.",
};

/** When Load more failed: «the next ones», as `MORE_BANNER` says it — not «page», the table's. */
export const MORE_FAILED = "The next campaigns could not be read.";

/**
 * Under the table: what is on screen, and nothing about what is not.
 *
 * "Loaded" is a fact about this screen — rows fetched and shown. It is never
 * "N of M", because there is no M: see `MORE_BANNER`.
 */
export function loadedLabel(shown: number, more: boolean): string {
	const rows = `${shown} ${shown === 1 ? "campaign" : "campaigns"} loaded`;
	return more ? `${rows}. X has more.` : `${rows}.`;
}

/** `created: true` — the body in the contract's own words. */
export const EXISTS = {
	title: "The campaign exists — do not retry, look it up.",
	body: "created: true means the object exists, whatever else the response says; do not retry — a retry creates a second campaign. Look the id up.",
};

/** `created: "unknown"` — the body in the contract's own words. */
export const UNKNOWN = {
	title: "The campaign may exist — do not retry yet.",
	body: "The create may have gone through; list the account's campaigns (by name) before retrying.",
};

export const READ_ONLY =
	"You can read this account's campaigns. Creating one, or switching one, needs write access to the account.";

/**
 * Under Create, while it is locked after `exists` or `unknown`.
 *
 * 🔴 It used to say "reload the list and look for this campaign by name". That was
 * an instruction the screen made impossible to follow: X's list lags behind a
 * create by minutes, so in exactly those minutes the campaign is missing from it
 * (twads#86), and the reload lifted the lock anyway (twads#87). The copy was right
 * and unfollowable at once — the worst kind, because nothing about it reads wrong.
 */
export const CREATE_LOCKED =
	"Create is locked: this campaign may already exist. X's list can take minutes to show a new campaign, so not seeing it here does not mean it was not created.";

/** The deliberate way out of the lock, for the answer that carries no id. */
export const CREATE_ANYWAY = {
	label: "Create another anyway",
	title: "This may create a second campaign.",
	body:
		"Nothing here can tell you whether the first one exists: the answer carried no id, and X's " +
		"list does not show a new campaign for the first few minutes. Check the account in X before " +
		"you do this. A second campaign can be deleted from its card once the list shows it, and only " +
		"while PAUSED — the reference: deleting is not reversible.",
	confirm: "I checked in X — create another",
};

/**
 * Said in the same place, because a lock someone cannot get past is a lock they
 * route around: this one lives in the page and a browser reload clears it. Better
 * that they learn it here, beside the warning, than discover it by pressing F5 and
 * never see the warning again.
 */
export const CREATE_LOCK_IS_LOCAL =
	"Reloading this page clears the warning — it is kept in the screen, not in the account.";

/**
 * What someone confirms before switching a campaign to ACTIVE. It names the campaign, the
 * ad account, both budgets as X holds them, and the X host the switch goes to. Switching
 * to PAUSED asks nothing.
 *
 * 🔴 It says what the switch is NOT: the campaign alone, never its line items, which stay
 * as X holds them. A campaign ACTIVE is a step without which nothing is delivered, said as
 * that and not as a promise of impressions — the path is `START_IS_NOT_DELIVERY`'s
 * (targeting-copy.ts), and this sentence adds nothing to it about X.
 */
export function enablePrompt(
	c: Pick<
		Campaign,
		"name" | "currency" | "dailyBudgetAmountLocalMicro" | "totalBudgetAmountLocalMicro"
	>,
	account: Pick<XAccount, "name" | "xAccountId">,
	host: XHost,
): string {
	const budget = `daily ${budgetText(c.dailyBudgetAmountLocalMicro, c.currency)}, total ${budgetText(c.totalBudgetAmountLocalMicro, c.currency)}`;
	// Two hosts are named without a caution: production, and the sandbox — proven to be X's
	// on 2026-09-18 (x-host.ts). A host this console cannot name, and one it has not read
	// yet, are still treated as production.
	const where =
		host.kind === "unknown"
			? "unknown — treat it as production"
			: host.kind === "other"
				? `${host.text} (not an X host this screen knows) — treat it as production`
				: host.text;
	return `Switch "${c.name}" to ACTIVE under ${account.name} · ${account.xAccountId}? Budget: ${budget}. X host: ${where}. This switches the campaign only: its line items stay as X holds them, and a PAUSED line item shows nothing. Nothing is delivered without the campaign ACTIVE, a line item ACTIVE and a promoted tweet.`;
}

/**
 * What stands beside Create when a campaign made there would be a real one, or when
 * the screen cannot tell. Three hosts warn: production, a host this console does not
 * name, and one it has not read yet — the last two because an unrecognised host is
 * treated as production for a write. The sandbox host does not, since 2026-09-18:
 * it is X's sandbox, proven there (x-host.ts). (The badge shows an unnamed host bare,
 * with no label, in warning.)
 */
export function hostWarning(host: XHost): { title: string; body: string } | null {
	if (host.kind === "production") {
		return {
			title: "Writes here go to X production.",
			body: `${host.host}: a campaign created on this screen is created on the real ad account.`,
		};
	}
	if (host.kind === "unknown") {
		return {
			title: "Which X this screen writes to could not be read — treat it as production.",
			body: "The Systems check on Overview names the host once it answers.",
		};
	}
	if (host.kind === "other") {
		return {
			title: "This screen writes to a host it does not know — treat it as production.",
			body: `${host.host}: neither X's production host nor its sandbox host.`,
		};
	}
	// The sandbox host carries no warning since 2026-09-18: it is X's sandbox, proven by a
	// test account created there (docs/START-HERE.md). Production, an unknown host and a
	// host this screen does not know still warn — those three have not changed.
	return null;
}

/** `servable` answers a different question from the status: whether X will deliver. */
export function servableText(servable: boolean, reasons: readonly string[]): string {
	if (servable) return "X will serve it.";
	return reasons.length > 0
		? `X will not serve it yet: ${reasons.join(", ")}.`
		: "X will not serve it yet, and gave no reason.";
}

export function createdText(id: string, servable: boolean, reasons: readonly string[]): string {
	return `Created PAUSED — ${id}. ${servableText(servable, reasons)} Switching it to ACTIVE is a separate step.`;
}

export function mismatchText(asked: TargetStatus, reported: string | null): string {
	return reported
		? `Asked for ${asked}; X reports ${reported}.`
		: `Asked for ${asked}; X returned no campaign.`;
}

/** A budget as X holds it: the number, in micro units of the currency. */
export function budgetText(value: number | null, currency: string | null): string {
	if (value === null) return "—";
	return `${String(value)} micro${currency ? ` ${currency}` : ""}`;
}

/**
 * 🔴 Where the next object is made, said on the screen where the owner stood
 * looking for it. On 2026-10-20 the owner wrote «I can create a campaign, but I
 * cannot add an ad group or an ad»: the line-item form is behind the row's
 * Line items button and a scroll, and the ad is two panels further in. Nothing on
 * this screen said so.
 *
 * 🔴 Rewritten by PR 3 of the one-screen layout (2026-10-29), because the places moved:
 * it said «the New line item form is at the bottom of that section, and the ad … is
 * attached under the line item's Creative button». The form is under the list in the Line
 * items panel, and the ad is attached on the line item's own screen.
 */
export const LINE_ITEMS_HERE =
	"A line item — X's ad group — is created under its campaign: press Line items on the campaign's row; the New line item form is under the list in that panel. The ad (a promoted tweet) is attached on the line item's own screen: Open on its row, then Promote in its Ads panel.";

/** Only instruments X says can fund, each named with its type and currency. */
export function fundingOptions(
	list: readonly FundingInstrument[],
): { value: string; label: string }[] {
	return list
		.filter((f) => f.ableToFund)
		.map((f) => ({
			value: f.id,
			label: `${f.description ?? f.id} · ${f.type} · ${f.currency ?? "no currency"}`,
		}));
}
